import {BaseStage} from "./BaseStage";
import {loading, stopLoading} from "../../../helpers/loading";
import Axios from "axios";
import createElement from "../../../helpers/createElement";
import handleAjaxErrorV2 from "../../../helpers/handleAjaxErrorV2";
import {TotalPreviewComponent} from "../TotalPreviewComponent/TotalPreviewComponent";

class TicketTypeSelectStage extends BaseStage {
    /** @var {TotalPreviewComponent} */
    totalPreviewComponent;

    constructor(globalState) {
        super(globalState);

        this.domContainer = document.querySelector(`.stage.${this.getStageKey()}`)

        // Clear state
        this.globalState.setReserveTicketCounts([]);
        this.globalState.setGeneralAdmissionTicketCounts([]);
        this.globalState.setAddons([]);
        this.globalState.setDiscountCode('');

        // Get route contents
        const getViewHtmlRoute = this.domContainer.dataset.getSeatTypesViewRoute;

        loading();

        const orderData = {
            orderType: this.globalState.selectedSeatType,
            event_id: this.globalState.selectedDate.id,
        }

        // Append selected seats if required
        if (this.globalState.selectedSeatType === 'reserved') {
            orderData.seats = this.globalState.selectedSeats;
        }

        Axios.post(
            getViewHtmlRoute,
            orderData
        )
            .then(response => {
                // Retrieve HTML from server
                const viewHtml = createElement(response.data.view);

                // Replace contents from server
                this.domContainer.innerHTML = viewHtml.innerHTML;

                // Run default components initializations
                this.initSharedComponents();

                this.initView();
            })
            .catch(error => {
                handleAjaxErrorV2(error)

                this.globalState.stageManager.previousStage();
                this.globalState.stageManager.clearStageInstance('seat-type-select');
            })
            .finally(stopLoading)
    }

    /**
     * @return {PublicTicketsFormDefs.Stage}
     */
    getStageKey() {
        // noinspection JSValidateTypes
        return 'seat-type-select';
    }

    initView() {
        // Init ticket count controls
        this.domContainer.querySelectorAll('.visitor_type_container .subtract-button').forEach(button => {
            button.addEventListener('click', () => {
                if (button.disabled === false) {
                    this.updateContainerCount(button.closest('.visitor_type_container'), -1)
                }
            })
        })

        this.domContainer.querySelectorAll('.visitor_type_container .add-button').forEach(button => {
            button.addEventListener('click', () => {
                if (button.disabled === false) {
                    this.updateContainerCount(button.closest('.visitor_type_container'), 1)
                }
            })
        })

        this.domContainer.querySelectorAll('.visitor_type_container input').forEach(input => {
            input.addEventListener('change', () => {
                this.cleanUpIntegerOnlyInput(input)
                if (input.closest('.reserved-tickets')) {
                    this.handleReservedTicketInputCustomBehavior(input)
                }
                this.handleTicketCountChange();
            })
            input.addEventListener('keyup', () => {
                this.cleanUpIntegerOnlyInput(input)
            })
        })

        // Init addon controls
        //// Setup addon select cards
        this.domContainer.querySelectorAll('.addon-container').forEach(showContainer => {
            const addonInput = showContainer.querySelector(`input`);

            addonInput.addEventListener('change', () => {
                this.handleAddonToggle(showContainer);
                this.handleAddonChange();
            })

            // Trigger show selection
            showContainer.addEventListener('click', (e) => {
                const clickedOnInput = e.target === addonInput;
                const clickedOnCountControl =
                    e.target.classList.contains('count-container') || // If we clicked on the count container
                    (e.target.closest('.count-container') !== null) // Or a children of it

                // If we clicked on neither the input or the count controls...
                if (clickedOnInput === false && clickedOnCountControl === false) {
                    // ... Toggle checkbox and dispatch event
                    addonInput.checked = !addonInput.checked;
                    addonInput.dispatchEvent(new InputEvent('change'));
                }
            })
        })

        //// Init addon count controls
        this.domContainer.querySelectorAll('.addon-container .subtract-button').forEach(button => {
            button.addEventListener('click', () => {
                this.updateContainerCount(button.closest('.count-container'), -1)
            })
        })

        this.domContainer.querySelectorAll('.addon-container .add-button').forEach(button => {
            button.addEventListener('click', () => {
                this.updateContainerCount(button.closest('.count-container'), 1)
            })
        })

        this.domContainer.querySelectorAll('.addon-container input').forEach(input => {
            input.addEventListener('change', () => {
                this.cleanUpIntegerOnlyInput(input)
                this.handleAddonChange();
            })
            input.addEventListener('keyup', () => {
                this.cleanUpIntegerOnlyInput(input)
            })
        })

        // Setup handlers to update totals on change
        this.domContainer.querySelectorAll('*[data-update-totals-on-change]').forEach(input => {
            input.addEventListener('change', () => {
                // We put this inside a set timeout to make sure it executes AFTER
                // all other handlers update the state
                setTimeout(() => {
                    this.updateTotals();
                }, 0)
            })
        })

        this.totalPreviewComponent = new TotalPreviewComponent(
            this.domContainer.querySelector('.totals-preview-wrapper'),
            this.globalState,
        )

        this.handleTicketCountChange();
        this.updateTotals();

        // Override default next stage button
        this.domContainer.querySelector('.next-stage-button')
            .addEventListener(
                'click', e => {
                    e.stopPropagation();
                    e.preventDefault();

                    // Check for cancellation insurance
                    let hasCancellationInsurance = false;
                    this.globalState.addons.forEach(addon => {
                        // Cancellation insure addon id
                        if (addon.addon_id === '2') {
                            hasCancellationInsurance = true;
                        }
                    })

                    if (hasCancellationInsurance) {
                        this.globalState.stageManager.nextStage();
                    } else {
                        Swal.fire({
                            title: 'Cancellation Insurance',
                            html: "The Promise does not offer refunds without cancellation insurance. This insurance provides for a refund in case <b style='text-decoration: underline'>The Promise</b> cancels the performance",
                            showCancelButton: true,
                            confirmButtonText: 'Continue',
                        }).then(result => {
                            if (result.isConfirmed) {
                                this.globalState.stageManager.nextStage();
                            }
                        })
                    }
                },
                {
                    capture: true,
                }
            )
    }

    handleTicketCountChange() {
        if (this.globalState.selectedSeatType === 'general-admission') {
            this.saveGeneralAdmissionTicketCount();
            if (this.globalState.generalAdmissionTicketCounts.length > 0) {
                this.enableNextStageButton();
            } else {
                this.disableNextStageButton();
            }
        } else {
            this.saveReserveTicketCount();

            let reservedTicketTotalCount = 0;

            this.globalState.reserveTicketCounts.forEach(ticket => {
                reservedTicketTotalCount += ticket.count
            })

            // Only enable next step if total tickets
            // matches number of selected seats
            if (reservedTicketTotalCount === this.globalState.selectedSeats.length) {
                this.enableNextStageButton();
            } else {
                this.disableNextStageButton();
            }
        }
    }


    saveGeneralAdmissionTicketCount() {
        /** @var {PublicTicketsFormDefs.GeneralAdmissionTicketCount[]} */
        const generalAdmissionTicketCount = [];

        // Get all general admission inputs
        this.domContainer.querySelectorAll('.general-admission-tickets .seat-type-input').forEach(input => {
            const count = parseInt(input.value);

            // Skip empty entries
            if (isNaN(count)) {
                return;
            }

            generalAdmissionTicketCount.push({
                count,
                visitorTypeId: input.dataset.visitorTypeId,
                section: input.dataset.section,
            })
        })

        this.globalState.setGeneralAdmissionTicketCounts(generalAdmissionTicketCount);
    }

    saveReserveTicketCount() {
        /** @var {PublicTicketsFormDefs.ReservedTicketCount[]} */
        const reservedTicketCounts = [];

        // Get all general admission inputs
        this.domContainer.querySelectorAll('.reserved-tickets .seat-type-input').forEach(input => {
            const count = parseInt(input.value);

            // Skip empty entries
            if (isNaN(count)) {
                return;
            }

            // noinspection JSCheckFunctionSignatures
            reservedTicketCounts.push({
                count,
                visitorTypeId: input.dataset.visitorTypeId,
                section: input.dataset.section,
                subSection: input.dataset.subSection,
            })
        })

        this.globalState.setReserveTicketCounts(reservedTicketCounts);
    }

    handleAddonChange() {
        /** @var {PublicTicketsFormDefs.Addon[]} */
        const addons = [];

        // Get all general admission inputs
        this.domContainer.querySelectorAll('.addon-container input[type="checkbox"]:checked').forEach(input => {
            const addonId = input.value;

            // Retrieve addon count if required
            if (input.dataset.withCount) {
                const count = parseInt(input.closest('.addon-container').querySelector('.addon-count-input').value);

                if (isNaN(count)) {
                    // Skip addon if count is not valid
                    return;
                }

                addons.push({
                    addon_id: addonId,
                    count
                })
            } else {
                addons.push({
                    addon_id: addonId,
                })
            }
        })

        this.globalState.setAddons(addons);
    }

    /**
     * @param {HTMLElement} container
     * @param {number} delta
     */
    updateContainerCount(container, delta) {
        const input = container.querySelector('input');

        let currentValue = parseInt(input.value);
        if (isNaN(currentValue)) {
            currentValue = 0;
        }

        const newValue = currentValue + delta;

        if (newValue <= 0) {
            input.value = ''
        } else {
            input.value = newValue.toFixed(0)
        }

        // Trigger change event on input
        input.dispatchEvent(new InputEvent('change'))
    }

    /**
     * @param {HTMLInputElement} input
     */
    cleanUpIntegerOnlyInput(input) {
        input.value = input.value.replace(/[^0-9]/g, '')
    }

    /**
     * @param {HTMLInputElement} input
     */
    handleReservedTicketInputCustomBehavior(input) {
        let inputValue = parseInt(input.value);

        if (isNaN(inputValue)) {
            inputValue = 0;
        }

        const parentSubSection = input.parentElement.closest('.reserved-subsection');
        const requiredTickets = parseInt(parentSubSection.dataset.ticketsRequired);

        // First, make sure we are not going out of bounds in the section
        let subSectionTotalTickets = 0;

        parentSubSection.querySelectorAll('.seat-type-input').forEach(input => {
            const ticketCount = parseInt(input.value);

            if (isNaN(ticketCount)) {
                return
            }

            subSectionTotalTickets += ticketCount;
        })

        // Subtract from section total the current input, so we can know what is
        // the maximum allowed value remaining
        subSectionTotalTickets -= inputValue;

        const maxRemaining = requiredTickets - subSectionTotalTickets;

        // If out of bounds, cap input value
        if (inputValue > maxRemaining) {
            inputValue = maxRemaining
        }

        if (inputValue) {
            // Update input with new max value
            input.value = inputValue.toFixed(0);
        } else {
            // Or clear the input
            input.value = ''
        }

        // Add back input value now that it has been capped
        subSectionTotalTickets += inputValue;

        // And update subsection header and buttons
        const remainingTicketsRequiredInSection = requiredTickets - subSectionTotalTickets;
        parentSubSection.querySelector('.section-seats-remaining').innerHTML = remainingTicketsRequiredInSection.toFixed(0);

        if (remainingTicketsRequiredInSection < 1) {
            // Disable all add buttons
            parentSubSection.querySelectorAll('.add-button').forEach(button => {
                button.disabled = true;
            })
        } else {
            // Enable all add buttons
            parentSubSection.querySelectorAll('.add-button').forEach(button => {
                button.disabled = false;
            })
        }
    }


    /**
     * @param {HTMLElement} showContainer
     */
    handleAddonToggle(showContainer) {
        /** @var {HTMLInputElement} */
        const addonInput = showContainer.querySelector(`input`);

        // Toggle selected class
        showContainer.classList.toggle('selected', addonInput.checked);

        if (addonInput.dataset.withCount) {
            showContainer.querySelector('.count-control-container')
                .classList
                .toggle('d-none', addonInput.checked === false)
        }
    }

    /**
     * Save ticket counts to memory
     */
    saveTickets() {

    }

    updateTotals() {
        this.totalPreviewComponent.updateTotalsFromServer().then();
    }
}

export {TicketTypeSelectStage};
